import { useRef, useState, useCallback, useEffect } from "react";
import Map, {
  Source,
  Layer,
  Popup,
  NavigationControl,
  type MapRef,
  type MapMouseEvent,
} from "react-map-gl/mapbox";
import {
  MAPBOX_TOKEN,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  MAP_STYLES,
  DEFAULT_MAP_STYLE,
  CLUSTER_COLORS,
  ACCURACY_COLORS,
} from "../../lib/map-config";
import { computeBounds } from "../../lib/map-bounds";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GeoJSONFeatureCollection = any;

interface ClusteredMapProps {
  geojsonData: GeoJSONFeatureCollection;
  onFeatureClick?: (feature: Record<string, unknown>) => void;
  className?: string;
  initialCenter?: [number, number];
  initialZoom?: number;
  showStyleToggle?: boolean;
  autoFitBounds?: boolean;
  boundsKey?: string;
}

export function ClusteredMap({
  geojsonData,
  onFeatureClick,
  className,
  initialCenter = DEFAULT_CENTER,
  initialZoom = DEFAULT_ZOOM,
  showStyleToggle = true,
  autoFitBounds = true,
  boundsKey,
}: ClusteredMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [mapStyle, setMapStyle] = useState<string>(DEFAULT_MAP_STYLE);
  const [popupInfo, setPopupInfo] = useState<{
    longitude: number;
    latitude: number;
    properties: Record<string, unknown>;
  } | null>(null);

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) return;

      if (feature.layer?.id === "clusters" && feature.properties?.cluster_id) {
        const map = mapRef.current?.getMap();
        if (!map) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const source = map.getSource("locations") as any;
        source.getClusterExpansionZoom(
          feature.properties.cluster_id,
          (_err: unknown, zoom: number | null) => {
            if (zoom == null) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const coords = (feature.geometry as any).coordinates as [
              number,
              number,
            ];
            map.easeTo({
              center: coords,
              zoom,
              duration: 500,
            });
          },
        );
      } else if (feature.layer?.id === "unclustered-point") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const coords = (feature.geometry as any).coordinates as [
          number,
          number,
        ];
        setPopupInfo({
          longitude: coords[0],
          latitude: coords[1],
          properties: (feature.properties ?? {}) as Record<string, unknown>,
        });
        onFeatureClick?.(feature as unknown as Record<string, unknown>);
      }
    },
    [onFeatureClick],
  );

  const onMouseEnter = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) map.getCanvas().style.cursor = "pointer";
  }, []);

  const onMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) map.getCanvas().style.cursor = "";
  }, []);

  useEffect(() => {
    if (!autoFitBounds) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const features = (geojsonData?.features ?? []) as {
      geometry?: { type?: string; coordinates?: unknown };
      properties?: Record<string, unknown> | null;
    }[];
    const bounds = computeBounds(features);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: 50, duration: 500, maxZoom: 14 });
    // `boundsKey` lets the caller trigger a refit (e.g. on search input change)
    // without changing the feature reference identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojsonData, boundsKey, autoFitBounds]);

  return (
    <div className={`relative h-full w-full ${className ?? ""}`}>
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: initialCenter[0],
          latitude: initialCenter[1],
          zoom: initialZoom,
        }}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={["clusters", "unclustered-point"]}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-right" />

        <Source
          id="locations"
          type="geojson"
          data={geojsonData}
          cluster={true}
          clusterMaxZoom={14}
          clusterRadius={50}
        >
          {/* Cluster circles */}
          <Layer
            id="clusters"
            type="circle"
            filter={["has", "point_count"]}
            paint={{
              "circle-color": [
                "step",
                ["get", "point_count"],
                CLUSTER_COLORS.small,
                10,
                CLUSTER_COLORS.medium,
                100,
                CLUSTER_COLORS.large,
              ],
              "circle-radius": [
                "step",
                ["get", "point_count"],
                20,
                10,
                25,
                100,
                30,
              ],
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
            }}
          />

          {/* Cluster counts */}
          <Layer
            id="cluster-count"
            type="symbol"
            filter={["has", "point_count"]}
            layout={{
              "text-field": ["get", "point_count_abbreviated"],
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
              "text-size": 12,
            }}
          />

          {/* Individual points */}
          <Layer
            id="unclustered-point"
            type="circle"
            filter={["!", ["has", "point_count"]]}
            paint={{
              "circle-color": [
                "match",
                ["get", "geocode_accuracy"],
                "rooftop",
                ACCURACY_COLORS.rooftop,
                "interpolated",
                ACCURACY_COLORS.interpolated,
                "approximate",
                ACCURACY_COLORS.approximate,
                "centroid",
                ACCURACY_COLORS.centroid,
                ACCURACY_COLORS.unknown,
              ],
              "circle-radius": 6,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
            }}
          />
        </Source>

        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            anchor="bottom"
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
          >
            <MapPopupCard properties={popupInfo.properties} />
          </Popup>
        )}
      </Map>

      {showStyleToggle && (
        <div className="absolute bottom-6 left-3 z-10 flex rounded-md border bg-background shadow-sm">
          <button
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mapStyle === MAP_STYLES.satellite
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            }`}
            onClick={() => setMapStyle(MAP_STYLES.satellite)}
          >
            Satellite
          </button>
          <button
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mapStyle === MAP_STYLES.light
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            }`}
            onClick={() => setMapStyle(MAP_STYLES.light)}
          >
            Streets
          </button>
        </div>
      )}
    </div>
  );
}

function MapPopupCard({ properties }: { properties: Record<string, unknown> }) {
  return (
    <div className="min-w-[200px] p-1">
      <p className="font-medium text-sm">
        {String(
          properties.formatted_address ??
            properties.street_line_1 ??
            "Unknown address",
        )}
      </p>
      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        {properties.location_type ? (
          <span className="capitalize">
            {String(properties.location_type).replace(/_/g, " ")}
          </span>
        ) : null}
        {properties.geocode_accuracy ? (
          <span className="capitalize">
            {String(properties.geocode_accuracy)}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {String(properties.city)}, {String(properties.state)}
      </div>
      {properties.id ? (
        <a
          href={`/locations/${String(properties.id)}`}
          className="mt-2 inline-block text-xs text-primary hover:underline"
        >
          View Details &rarr;
        </a>
      ) : null}
    </div>
  );
}
